'use client';

import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { stringifyStoreId } from '@libs/core';
import { getCookieValue } from '@libs/common-next';
import { StoreContext, StoreDispatchContext } from './context';
import { STORE_COOKIE_NAME } from './constants';
import { setStoreCookie } from './utils';
import type { StoreProviderDispatch, StoreProviderState } from './types';
import { useStoreQuery } from './queries';

/**
 * In the previous implementation there was no difference between selected and favorite stores
 * That's why we are not supporting favorite store any more.
 *
 * In the future, we will extend this provider with favorite and any other types of Stores that are added
 */
export function StoreProvider({ children }: Readonly<{ children: ReactNode }>) {
    const [selectedStoreId, setSelectedStoreId] = useState(() =>
        IS_SSR ? undefined : getCookieValue(STORE_COOKIE_NAME)
    );

    const selectedStore = useStoreQuery(selectedStoreId);

    const updateSelectedStore = useCallback((storeId: string | number) => {
        const parsedStoreId = typeof storeId === 'number' ? (stringifyStoreId(storeId) as string) : storeId;

        setStoreCookie(parsedStoreId);
        setSelectedStoreId(parsedStoreId);
    }, []);

    const stateValue = useMemo<StoreProviderState>(
        () => ({
            selectedStore,
            selectedStoreId,
        }),
        [selectedStore]
    );

    const dispatchValue = useMemo<StoreProviderDispatch>(
        () => ({
            updateSelectedStore,
        }),
        [updateSelectedStore]
    );

    return (
        <StoreContext.Provider value={stateValue}>
            <StoreDispatchContext.Provider value={dispatchValue}>{children}</StoreDispatchContext.Provider>
        </StoreContext.Provider>
    );
}
